import posthog from 'posthog-js'
import type { User } from './api'

/**
 * PostHog — the free-tier product suite, wired end-to-end.
 *
 * What this file turns on (all inside PostHog's free monthly allowance):
 *   • Product analytics ....... autocapture + custom events (1M events/mo)
 *   • Web analytics ........... SPA pageviews / pageleaves (billed with events)
 *   • Session replay .......... 5K recordings/mo, masked by default
 *   • Feature flags ........... 1M requests/mo
 *   • Experiments (A/B tests) . built on feature flags, same allowance
 *   • Error tracking .......... 100K exceptions/mo
 *   • Surveys ................. 1500 responses/mo
 *   • Group analytics ......... roll metrics up per company
 *
 * See POSTHOG_INTEGRATION.md for setup and the credentials each needs.
 *
 * ── Overlap with Sentry ──────────────────────────────────────────────────
 * Sentry (`lib/sentry.ts`) also does error tracking and session replay. Both
 * are deliberately left switched on because they answer different questions
 * (Sentry: stack traces + release health; PostHog: which flag/experiment the
 * user was in, and the replay next to the funnel they dropped out of). If you
 * do not want to pay the double bandwidth cost, turn ONE of them off:
 *   VITE_POSTHOG_SESSION_REPLAY=false   (keep Sentry replay)
 *   VITE_SENTRY_REPLAY_SESSION_RATE=0   (keep PostHog replay)
 * A console warning fires at init when both are recording.
 *
 * ── Inert by default ─────────────────────────────────────────────────────
 * Nothing initialises unless `VITE_POSTHOG_API_KEY` is set AND the build is a
 * production build — so `pnpm dev` never sends events. Set
 * `VITE_POSTHOG_ENABLE_IN_DEV=true` to opt a local run in for testing.
 */

let initialized = false

/** Parse a Vite string env var as a boolean, falling back when unset/blank. */
function envBool(raw: string | undefined, fallback: boolean): boolean {
  if (raw === undefined || raw === null || raw === '') return fallback
  return raw === 'true' || raw === '1'
}

/**
 * Strip credentials out of any URL-ish property before it leaves the browser.
 *
 * Several flows in this app put one-shot secrets in the query string — the
 * WorkOS SSO `?code=`, email-verification `?token=`, and password-reset
 * `?token=` links all land on a real route, so `$current_url` would otherwise
 * carry a live credential into analytics, replay and error events.
 */
const SENSITIVE_QUERY_PARAMS = [
  'token',
  'access_token',
  'refresh_token',
  'code',
  'api_key',
  'apikey',
  'secret',
  'password',
]

export function scrubUrl(url: unknown): unknown {
  if (typeof url !== 'string' || !url.includes('?')) return url
  try {
    const parsed = new URL(url, window.location.origin)
    let touched = false
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[redacted]')
        touched = true
      }
    }
    return touched ? parsed.toString() : url
  } catch {
    // Not a parseable URL — leave it alone rather than dropping the property.
    return url
  }
}

const URL_PROPERTIES = ['$current_url', '$referrer', '$pathname', '$initial_current_url']

/** `sanitize_properties` hook — runs on every event before it is queued. */
export function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  for (const key of URL_PROPERTIES) {
    if (key in properties) properties[key] = scrubUrl(properties[key])
  }
  return properties
}

export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_API_KEY
  if (!key) return

  const enableInDev = envBool(import.meta.env.VITE_POSTHOG_ENABLE_IN_DEV, false)
  if (!import.meta.env.PROD && !enableInDev) return

  // Session replay and error tracking are the two products that can meaningfully
  // duplicate Sentry, so both are individually switchable.
  const replayEnabled = envBool(import.meta.env.VITE_POSTHOG_SESSION_REPLAY, true)
  const errorTrackingEnabled = envBool(
    import.meta.env.VITE_POSTHOG_ERROR_TRACKING,
    true,
  )
  // This product renders customer PII on nearly every screen (contact names and
  // emails, campaign copy, inbox threads), so replay masks all input values by
  // default. Flip to `false` only if you have decided the replays are worth the
  // retained PII. Regardless of this setting, add `ph-no-capture` to any element
  // that must never be recorded.
  const maskAllInputs = envBool(import.meta.env.VITE_POSTHOG_MASK_ALL_INPUTS, true)

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',

    // ── Web analytics + user paths ──────────────────────────────────────
    // Capture SPA pageviews and page-leaves automatically off the History API
    // (react-router uses pushState), so we don't hand-instrument every route.
    // Powers pageviews, sources, bounce, geography and the "user paths" view.
    capture_pageview: 'history_change',
    capture_pageleave: true,

    // ── Product analytics ───────────────────────────────────────────────
    // Autocapture clicks/inputs/submits — this powers heatmaps and lets
    // funnels/trends be built from real interactions with zero manual
    // instrumentation. Custom events go through `captureEvent()` below.
    autocapture: true,
    enable_heatmaps: true,

    // ── Surveys ─────────────────────────────────────────────────────────
    // In-app NPS / feedback surveys are created and targeted from the PostHog
    // dashboard and render client-side with no extra code here.
    disable_surveys: false,

    // ── Session replay ──────────────────────────────────────────────────
    // Sampling (what % of sessions to record) is a project setting in the
    // PostHog UI, not a client option — keep it low there to stay inside the
    // 5K recordings/mo free tier. Masking, however, must be decided here
    // because it happens in the browser before anything is transmitted.
    disable_session_recording: !replayEnabled,
    session_recording: {
      maskAllInputs,
      maskInputOptions: {
        // Always masked, even when maskAllInputs is off — these are the fields
        // where a leak is a credential leak rather than a privacy annoyance.
        password: true,
      },
      // Opt-in escape hatches for individual elements anywhere in the app.
      maskTextSelector: '.ph-mask',
      blockSelector: '.ph-block',
    },

    // ── Error tracking ──────────────────────────────────────────────────
    // Unhandled errors and promise rejections become `$exception` events, which
    // PostHog groups into issues. Console errors stay OFF: this app logs
    // expected, already-handled failures via console.error (see ErrorBoundary
    // and the axios interceptors), and capturing those would burn the 100K/mo
    // exception allowance on noise.
    capture_exceptions: errorTrackingEnabled && {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },

    // ── Person profiles ─────────────────────────────────────────────────
    // Build profiles for EVERY visitor, not just signed-in users — so anonymous
    // traffic (landing/login/register pages, people who never sign up) still
    // shows up in web analytics, paths and funnels. When such a visitor later
    // logs in, `identify()` merges their anonymous history into the now-known
    // person automatically.
    person_profiles: 'always',

    // Never let a one-shot credential in a query string reach PostHog.
    sanitize_properties: sanitizeProperties,
  })

  initialized = true

  // Both replay recorders running at once doubles upload bandwidth and stores
  // the same PII twice. Surface it loudly rather than letting it go unnoticed.
  if (
    replayEnabled &&
    import.meta.env.VITE_SENTRY_DSN &&
    Number(import.meta.env.VITE_SENTRY_REPLAY_SESSION_RATE ?? 0.1) > 0
  ) {
    console.warn(
      '[posthog] PostHog AND Sentry session replay are both recording. ' +
        'Set VITE_POSTHOG_SESSION_REPLAY=false or VITE_SENTRY_REPLAY_SESSION_RATE=0.',
    )
  }
}

/**
 * Identify (or reset) the signed-in user for product analytics. Mirrors the
 * Sentry `setUser` wiring in `main.tsx` so login/SSO/logout transitions keep
 * PostHog's person — and the company "group" — in sync. No-op until init runs.
 *
 * The distinct id is `String(user.id)`, which the backend
 * (`app/core/posthog_client.py`) also uses — that is what stitches server-side
 * events onto the same person as the browser's.
 */
export function identifyPostHog(user: User | null): void {
  if (!initialized) return

  if (user) {
    posthog.identify(String(user.id), {
      email: user.email,
      name: user.full_name,
      username: user.username,
    })
    // Group analytics: roll metrics up by company/organization, not just the
    // individual user. Uses company_name as the group key when present.
    if (user.company_name) {
      posthog.group('company', user.company_name, { name: user.company_name })
    }
    // Flags can target person/group properties, and the ones above only became
    // known just now — re-evaluate so a flag gated on e.g. company doesn't stay
    // false for the rest of the session.
    posthog.reloadFeatureFlags()
  } else {
    // Logout — drop the identity so the next user starts a clean profile.
    posthog.reset()
  }
}

/**
 * Capture a custom product event (funnels / trends). Safe no-op when PostHog
 * isn't initialised (dev / no key), so call sites don't need their own guards.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  posthog.capture(event, properties)
}

/**
 * Report a handled error to PostHog Error Tracking.
 *
 * Unhandled errors are already captured automatically by `capture_exceptions`;
 * use this for errors you caught and recovered from but still want grouped into
 * an issue — e.g. a React render error swallowed by an ErrorBoundary.
 */
export function captureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  posthog.captureException(error, properties)
}

/** Read a boolean feature flag. Returns false when PostHog isn't initialised. */
export function isFeatureEnabled(flag: string): boolean {
  if (!initialized) return false
  return posthog.isFeatureEnabled(flag) ?? false
}

/** Read a multivariate feature flag's variant key, or undefined. */
export function getFeatureFlag(flag: string): string | boolean | undefined {
  if (!initialized) return undefined
  return posthog.getFeatureFlag(flag)
}

/** Read the JSON payload attached to a feature flag, or undefined. */
export function getFeatureFlagPayload(flag: string): unknown {
  if (!initialized) return undefined
  return posthog.getFeatureFlagPayload(flag)
}

/**
 * Resolve which variant of an A/B test (PostHog Experiment) this user is in.
 *
 * Experiments are feature flags underneath: PostHog assigns each person a
 * variant key, and reading it here emits `$feature_flag_called`, which is the
 * exposure event the experiment's statistics are computed from. So simply
 * calling this at the point where the variants diverge is the whole
 * instrumentation — no separate "track exposure" call is needed.
 *
 * Returns `fallback` ("control" by default) when PostHog is off or the flag has
 * not loaded yet, so an experiment can never leave the UI in a broken state.
 */
export function getExperimentVariant(
  experimentKey: string,
  fallback = 'control',
): string {
  if (!initialized) return fallback
  const variant = posthog.getFeatureFlag(experimentKey)
  return typeof variant === 'string' ? variant : fallback
}

/**
 * Record a goal/conversion for an experiment.
 *
 * PostHog can use any existing event as an experiment goal metric, so this is
 * just a normal capture — the helper exists to keep the `$feature_flag_response`
 * property attached, which is what lets the goal be broken down by variant even
 * for users whose exposure happened on an earlier page.
 */
export function trackExperimentGoal(
  experimentKey: string,
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  posthog.capture(event, {
    ...properties,
    $feature_flag: experimentKey,
    $feature_flag_response: posthog.getFeatureFlag(experimentKey),
  })
}

/**
 * Subscribe to feature-flag (re)loads. Returns an unsubscribe function; returns
 * a no-op unsubscribe when PostHog isn't initialised.
 *
 * Flags arrive asynchronously after page load, so anything that must react to
 * the *first* real value (rather than the default) should use this.
 */
export function onFeatureFlags(
  callback: (flags: string[]) => void,
): () => void {
  if (!initialized) return () => {}
  return posthog.onFeatureFlags(callback)
}

/** Start session replay on demand (e.g. only for users who opted in). */
export function startSessionRecording(): void {
  if (!initialized) return
  posthog.startSessionRecording()
}

/** Stop session replay for the rest of this session. */
export function stopSessionRecording(): void {
  if (!initialized) return
  posthog.stopSessionRecording()
}

/**
 * URL of the replay for the current session, for pasting into a bug report or
 * attaching to a support ticket. Undefined when replay isn't running.
 */
export function getSessionReplayUrl(): string | undefined {
  if (!initialized) return undefined
  return posthog.get_session_replay_url?.({ withTimestamp: true }) ?? undefined
}

/** True once `initPostHog()` has actually initialised the SDK. */
export function isPostHogInitialized(): boolean {
  return initialized
}

/** Test-only hook: reset module state between tests. */
export function __resetPostHogForTests(): void {
  initialized = false
}

/** The raw client, for the `posthog-js/react` provider and hooks. */
export { posthog }
